'use client';

import { Logger, LogLevel } from '@logtail/next';
import { useEffect, useMemo } from 'react';
import { isAutomationRuntime } from '@/lib/runtime-mode';

type LoggerOptions = ConstructorParameters<typeof Logger>[0];

type ClientLogger = Pick<Logger, 'debug' | 'info' | 'warn' | 'error' | 'flush'>;

const noop = () => {
  // intentionally empty
};

const noopAsync = async () => {
  // intentionally empty
};

const createNoopLogger = (): ClientLogger => ({
  debug: noop,
  info: noop,
  warn: noop,
  error: noop,
  flush: noopAsync,
});

export function useLogger(options?: LoggerOptions): ClientLogger {
  const isWebDriver = typeof navigator !== 'undefined' && navigator.webdriver;
  const shouldDisable = isAutomationRuntime() || isWebDriver || options?.logLevel === LogLevel.off;

  const noopLogger = useMemo(() => createNoopLogger(), []);

  const logger = useMemo(() => {
    if (shouldDisable) {
      return noopLogger;
    }
    return new Logger(options ?? {});
  }, [noopLogger, options, shouldDisable]);

  useEffect(() => {
    return () => {
      logger.flush().catch(() => {
        // best-effort client flush during teardown
      });
    };
  }, [logger]);

  return logger;
}

export { LogLevel } from '@logtail/next';
